import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";

import { fetcherAuth } from "utils/api";

const MAX_BRANDS_LIMIT = 400;

interface Props {
  open: boolean;
  handleClose: () => void;
}

enum WeekSelection {
  this_week = "this_week",
  next_week = "next_week",
  none = "none",
}

export default function RunPitchScheduleDialog({ open, handleClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [numberOfBrands, setNumberOfBrands] = useState<number>(0);
  const [numberOfRepitchBrands, setNumberOfRepitchBrands] = useState<number>(0);
  const [validation, setValidation] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [weekSelection, setWeekSelection] = useState<WeekSelection>(
    WeekSelection.none,
  );

  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const validateFields = () => {
    if (
      numberOfBrands === undefined ||
      numberOfBrands === null ||
      numberOfBrands < 0 ||
      numberOfBrands > MAX_BRANDS_LIMIT
    ) {
      setValidation("numberOfBrands");
      setValidationMessage(
        `The number you select must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (
      numberOfRepitchBrands === undefined ||
      numberOfRepitchBrands === null ||
      numberOfRepitchBrands < 0 ||
      numberOfRepitchBrands > MAX_BRANDS_LIMIT
    ) {
      setValidation("numberOfRepitchBrands");
      setValidationMessage(
        `The number you select must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (numberOfBrands === 0 && numberOfRepitchBrands === 0) {
      setValidation("general");
      setValidationMessage(
        "You must select at least one brand to email or re-pitch.",
      );
      return false;
    }

    if (weekSelection === WeekSelection.none) {
      setValidation("general");
      setValidationMessage(
        `Please select if you want to send emails this or next week.`,
      );
      return false;
    }

    setValidation("");
    setValidationMessage("");
    return true;
  };

  const handleRun = async () => {
    if (!validateFields()) return;

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings/run`,
        "POST",
        {},
        {
          numberOfBrands,
          numberOfRepitchBrands,
          weekSelection,
        },
      );

      setAlert("Successfully started pitch scheduling", "success");
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: number) => {
    if (field === "numberOfBrands") {
      setNumberOfBrands(value);
    } else {
      setNumberOfRepitchBrands(value);
    }

    if (value >= 0 && value <= MAX_BRANDS_LIMIT) {
      setValidation("");
      setValidationMessage("");
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Run Pitch Schedule</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <strong>How many emails do you want to send to new brands?</strong>
            {validation === "numberOfBrands" ? (
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            ) : (
              <FormHelperText>
                Select a number between 0 and {MAX_BRANDS_LIMIT}. Emails will be
                sent between Monday - Thursday.
              </FormHelperText>
            )}
            <TextField
              sx={{ width: 120, mt: 1 }}
              size="small"
              variant="outlined"
              type="number"
              placeholder="0"
              value={numberOfBrands}
              onChange={(e) => {
                handleFieldChange(
                  "numberOfBrands",
                  Math.floor(Number(e.target.value)),
                );
              }}
              inputProps={{ min: 0, max: MAX_BRANDS_LIMIT }}
            />
          </Grid>

          <Grid item xs={12}>
            <strong>How many re-pitching emails do you want to send?</strong>
            {validation === "numberOfRepitchBrands" ? (
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            ) : (
              <FormHelperText>
                Select a number between 0 and {MAX_BRANDS_LIMIT}. Emails will be
                sent between Monday - Thursday to already pitched brands that
                you have never gotten a response and you haven't sent any emails
                to in the last 30 days.
              </FormHelperText>
            )}
            <TextField
              sx={{ width: 120, mt: 1 }}
              size="small"
              variant="outlined"
              type="number"
              placeholder="0"
              value={numberOfRepitchBrands}
              onChange={(e) => {
                handleFieldChange(
                  "numberOfRepitchBrands",
                  Math.floor(Number(e.target.value)),
                );
              }}
              inputProps={{ min: 0, max: MAX_BRANDS_LIMIT }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>
              <strong>When do you want to send these emails?</strong>
            </Typography>
            <Select
              onChange={(event: SelectChangeEvent<WeekSelection>) => {
                setWeekSelection(event.target.value as WeekSelection);
              }}
              value={weekSelection}
              size="small"
            >
              <MenuItem value={WeekSelection.none}>
                <em>Please enter a value</em>
              </MenuItem>
              <MenuItem value={WeekSelection.this_week}>This Week</MenuItem>
              <MenuItem value={WeekSelection.next_week}>Next Week</MenuItem>
            </Select>
          </Grid>

          {validation === "general" && (
            <Grid item xs={12}>
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          disableElevation
          onClick={handleRun}
          loading={loading}
        >
          Run
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
